import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { gradeText } from "@/lib/gradeLabel";

/**
 * My Learning — every course this student is enrolled in.
 *
 * Deliberately plainer than the trail. This screen is for choosing; the path is
 * where the momentum lives.
 */

/** Null percent means nothing has been marked yet — show "No marks yet",
 *  never 0%. A student who has not been graded has not scored nothing. */
export type StudentGrade = {
  pointsEarned: number | null;
  pointsPossible: number | null;
  percent: number | null;
  letter?: string | null;
  gradedCount: number;
  awaitingCount: number;
  weighted?: boolean;
  buckets?: {
    key: string;
    label: string;
    weight: number;
    percent: number;
    pointsEarned: number | null;
    pointsPossible: number | null;
    gradedCount: number;
  }[];
};

export type CourseSyllabusFile = {
  mediaId?: string;
  url: string;
  name: string;
  mime?: string;
};

export type StudentCourse = {
  id: string;
  title: string;
  description: string | null;
  percent: number;
  activitiesDone: number;
  activitiesTotal: number;
  hasPublishedWork: boolean;
  grade?: StudentGrade;
  gradeWeights?: Record<string, number>;
  objectives?: string[];
  syllabus?: CourseSyllabusFile | null;
};

export default function MyLearning() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<StudentCourse[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api<{ success: boolean; data: StudentCourse[] }>("/api/student/courses")
      .then((env) => {
        if (!cancelled) setCourses(env.data || []);
      })
      .catch((err) => {
        if (!cancelled) setError(errorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="student-page">
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!courses) {
    return <LearningOpeningSkeleton />;
  }

  return (
    <div className="student-page">
      <div className="student-page-head">
        <span className="student-eyebrow">My Learning</span>
        <h1>
          {courses.length === 0
            ? "Your courses"
            : `${courses.length} ${courses.length === 1 ? "course" : "courses"}`}
        </h1>
        {courses.length > 0 && <p>Pick up where you left off.</p>}
      </div>

      {courses.length === 0 ? (
        <div className="student-empty">
          <div>
            <span className="student-empty__icon" aria-hidden>
              <BookOpen size={26} />
            </span>
            <strong>No courses yet</strong>
            <p>Courses appear here once your school enrolls you in them.</p>
          </div>
        </div>
      ) : (
        <div className="student-course-grid">
          {courses.map((course) => (
            <button
              key={course.id}
              type="button"
              className="student-course-card"
              onClick={() => navigate(`/student/course/${course.id}`)}
            >
              <div className="student-course-card__top">
                <span>Course</span>
                {course.grade?.percent != null ? (
                  <span title="Your grade on marked work">{gradeText(course.grade)}</span>
                ) : course.hasPublishedWork ? (
                  <span className="student-course-card__nomark">No marks yet</span>
                ) : null}
              </div>
              <h3>{course.title}</h3>
              <p>
                {course.hasPublishedWork
                  ? `${course.activitiesDone} of ${course.activitiesTotal} activities`
                  : "Nothing to do yet"}
              </p>
              {course.hasPublishedWork && (
                <div className="student-meter">
                  <i style={{ width: `${course.percent}%` }} />
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LearningOpeningSkeleton() {
  return (
    <div className="student-page" aria-busy="true" aria-live="polite">
      <span className="student-vh">Loading your courses</span>
      <div className="student-page-head">
        <div className="student-ghost student-ghost--chip" />
        <div className="student-ghost student-ghost--title" />
        <div className="student-ghost student-ghost--line student-ghost--w60" style={{ marginTop: 10 }} />
      </div>
      <div className="student-course-grid">
        <GhostCourseCard />
        <GhostCourseCard />
        <GhostCourseCard />
        <GhostCourseCard />
      </div>
    </div>
  );
}

function GhostCourseCard() {
  return (
    <div className="student-ghost-course" aria-hidden>
      <div className="student-ghost student-ghost--chip" />
      <div className="student-ghost student-ghost--line student-ghost--w80" />
      <div className="student-ghost student-ghost--meta" />
      <div className="student-ghost student-ghost--meter" />
    </div>
  );
}
